// IRO operational detectors — Phase 1 (4 detectors)
//
// Pure functions: no I/O, no side effects, no DB calls.
// Each detector receives:
//   - rows:     array of building_sensors records (already loaded by the parent)
//   - settings: row from operational_iro_settings (project calibration)
// Each detector returns either NULL (no IRO) or an IRO object ready to upsert.
//
// IRO object shape:
//   {
//     detector_id, type, esrs_datapoint, category,
//     period_year, period_month,
//     severity, likelihood, time_horizon, materiality,
//     financial_estimate_eur,
//     evidence_rows, literal_quote, detector_output
//   }
//
// The component handles persistence (project_id, user_id, status, evidence_files).

// ── Helpers ──────────────────────────────────────────────────────────────────

// Rolling window of the last N months ending at the most recent reading.
// Returns { rows, endYear, endMonth, monthsCovered } or null if not enough data.
function rollingWindow(rows, months = 12) {
  if (!rows || rows.length === 0) return null
  const sorted = [...rows].sort((a, b) => {
    if (b.reading_year !== a.reading_year) return b.reading_year - a.reading_year
    return b.reading_month - a.reading_month
  })
  const end = sorted[0]
  const window = sorted.slice(0, months)
  return {
    rows: window,
    endYear: end.reading_year,
    endMonth: end.reading_month,
    monthsCovered: window.length,
  }
}

function sumField(rows, key) {
  return rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)
}

// Severity bands by % excess over baseline (used by D1)
function severityFromExcessPct(pct) {
  if (pct < 0.10) return 0   // below threshold = no IRO
  if (pct < 0.20) return 1
  if (pct < 0.30) return 2
  if (pct < 0.50) return 3
  if (pct < 0.75) return 4
  return 5
}

// Severity bands by € exposure (used by D7)
function severityFromEur(eur) {
  if (eur < 1000)  return 1
  if (eur < 5000)  return 2
  if (eur < 20000) return 3
  if (eur < 50000) return 4
  return 5
}

function evidenceIds(rows) {
  return rows.map(r => r.id).filter(Boolean)
}

// Parsea una línea de notes del building_sensors al formato estándar de import:
//   "kind: consumption_unit (amount_eur€) — supplier"
// Tolera ausencia de € y de suministrador. Devuelve [] si no parsea.
function parseInvoiceCitations(noteText) {
  if (!noteText) return []
  const entries = String(noteText).split('|').map(s => s.trim()).filter(Boolean)
  const out = []
  for (const e of entries) {
    const m = e.match(/^(\w+)\s*:\s*([\d.,]+)\s*(m³|m3|kWh|t|kg)\s*(?:\(([\d.,]+)\s*€\))?\s*(?:[—-]\s*(.+))?$/i)
    if (!m) continue
    const c = parseFloat(String(m[2]).replace(',', '.'))
    const a = m[4] ? parseFloat(String(m[4]).replace(',', '.')) : null
    out.push({
      kind: m[1].toLowerCase(),
      consumption: isFinite(c) ? c : null,
      unit: m[3].replace('m3', 'm³'),
      amount_eur: isFinite(a) ? a : null,
      supplier: (m[5] || '').trim() || null,
    })
  }
  return out
}

const KIND_LABEL_ES = { electricity: 'Electricidad', water: 'Agua', gas: 'Gas natural', heat: 'Calefacción urbana' }

// Construye el resumen agregado y citable de un conjunto de building_sensors.
// Devuelve { period, totals, invoices, text } o null si rows está vacío.
// `text` es la cita literal multilínea que se vuelca en `literal_quote`.
// `totals`, `invoices` y `period` se vuelcan en `detector_output.period_summary`.
function buildPeriodSummary(rows) {
  if (!rows || rows.length === 0) return null

  const sorted = [...rows].sort((a, b) => {
    if (a.reading_year !== b.reading_year) return a.reading_year - b.reading_year
    return a.reading_month - b.reading_month
  })
  const first = sorted[0]
  const last  = sorted[sorted.length - 1]

  const totals = {
    electricity_kwh:      Math.round(sumField(rows, 'electricity_kwh')),
    gas_kwh:              Math.round(sumField(rows, 'gas_kwh')),
    district_heat_kwh:    Math.round(sumField(rows, 'district_heat_kwh')),
    energy_kwh_renewable: Math.round(sumField(rows, 'energy_kwh_renewable')),
    water_m3_total:       Math.round(sumField(rows, 'water_m3_total')),
    waste_tons_total:     +sumField(rows, 'waste_tons_total').toFixed(2),
    scope1_kgco2e:        Math.round(sumField(rows, 'scope1_kgco2e')),
    scope2_kgco2e:        Math.round(sumField(rows, 'scope2_kgco2e')),
    scope3_kgco2e:        Math.round(sumField(rows, 'scope3_kgco2e')),
  }

  // Agregamos las facturas parseadas del campo notes por (kind, supplier)
  const supplierMap = new Map()
  for (const r of rows) {
    for (const inv of parseInvoiceCitations(r.notes)) {
      const key = `${inv.kind}||${inv.supplier || '(sin suministrador)'}`
      const agg = supplierMap.get(key) || {
        kind: inv.kind, supplier: inv.supplier,
        count: 0, total_consumption: 0, total_eur: 0, unit: inv.unit,
      }
      agg.count += 1
      agg.total_consumption += (inv.consumption || 0)
      agg.total_eur += (inv.amount_eur || 0)
      supplierMap.set(key, agg)
    }
  }
  const invoices = [...supplierMap.values()]
    .map(i => ({ ...i, total_consumption: +i.total_consumption.toFixed(2), total_eur: +i.total_eur.toFixed(2) }))
    .sort((a, b) => b.total_eur - a.total_eur)

  // Texto citable multilínea
  const fmt = (n) => Math.round(n).toLocaleString('es-ES')
  const periodStart = `${first.reading_year}-${String(first.reading_month).padStart(2, '0')}`
  const periodEnd   = `${last.reading_year}-${String(last.reading_month).padStart(2, '0')}`

  const lines = []
  lines.push(`Periodo: ${periodStart} a ${periodEnd} · ${rows.length} ${rows.length === 1 ? 'mes' : 'meses'}`)
  const energyBits = []
  if (totals.electricity_kwh > 0)      energyBits.push(`${fmt(totals.electricity_kwh)} kWh red`)
  if (totals.gas_kwh > 0)              energyBits.push(`${fmt(totals.gas_kwh)} kWh gas`)
  if (totals.district_heat_kwh > 0)    energyBits.push(`${fmt(totals.district_heat_kwh)} kWh calef. urbana`)
  if (totals.energy_kwh_renewable > 0) energyBits.push(`${fmt(totals.energy_kwh_renewable)} kWh FV`)
  if (energyBits.length) lines.push(`Energía: ${energyBits.join(' + ')}`)
  if (totals.water_m3_total > 0) lines.push(`Agua: ${fmt(totals.water_m3_total)} m³`)
  if (totals.waste_tons_total > 0) lines.push(`Residuos: ${totals.waste_tons_total.toFixed(1)} t`)
  if ((totals.scope1_kgco2e + totals.scope2_kgco2e) > 0)
    lines.push(`Scope 1+2: ${fmt((totals.scope1_kgco2e + totals.scope2_kgco2e) / 1000)} tCO₂e`)
  if (invoices.length) {
    lines.push(`Facturas analizadas (${invoices.reduce((s, i) => s + i.count, 0)}):`)
    for (const inv of invoices) {
      const label = KIND_LABEL_ES[inv.kind] || inv.kind
      const euros = inv.total_eur > 0 ? ` · ${fmt(inv.total_eur)} €` : ''
      lines.push(`  · ${label}: ${fmt(inv.total_consumption)} ${inv.unit} · ${inv.count} ${inv.count === 1 ? 'factura' : 'facturas'}${euros} · ${inv.supplier || '(sin suministrador)'}`)
    }
  }

  return {
    period: { start: periodStart, end: periodEnd, months: rows.length },
    totals, invoices,
    text: lines.join('\n'),
  }
}

// ── D3 · Estrés hídrico regional ─────────────────────────────────────────────
// Risk · ESRS E3-4 (Consumo de agua en zonas de estrés hídrico) + DNSH Taxonomía UE
//
// Detecta si el activo se encuentra en una demarcación / cuenca hidrográfica con
// nivel de estrés hídrico ≥ Alerta según el Plan Especial de Sequías vigente,
// y cruza esa información con la intensidad hídrica real del edificio
// (m³ / m² · año) obtenida de las facturas de agua cargadas.
//
// Umbrales de referencia por tipología (m³ / m² · año) — orientación GRESB Water
// Indicator:  hotel 2,5 · residencial 1,5 · retail 0,8 · oficinas 0,5. Si el
// activo no declara tipología se usa un piso genérico de 1,0.
//
// El detector recibe `waterStressInfo` como argumento adicional inyectado por
// runAllDetectors:  { level, region, source, source_date }
// level ∈ 'normalidad' | 'prealerta' | 'alerta' | 'excepcionalidad' | 'emergencia'
export function detectEstresHidrico(rows, settings, waterStressInfo) {
  if (!waterStressInfo || !waterStressInfo.level) return null
  const level = String(waterStressInfo.level).toLowerCase().trim()
  if (level === 'normalidad' || level === 'prealerta' || level === 'none') return null

  const m2 = Number(settings?.gross_area_m2) || 0
  if (m2 <= 0) return null

  const period = buildPeriodSummary(rows)
  if (period.period.months < 12) return null
  const water_m3 = period.totals.water_m3_total
  if (water_m3 <= 0) return null

  const intensity = water_m3 / m2

  const typology = (settings?.building_typology || 'default').toLowerCase()
  const thresholds = {
    hotel: 2.5,
    hoteles: 2.5,
    residencial: 1.5,
    residential: 1.5,
    oficinas: 0.5,
    office: 0.5,
    retail: 0.8,
    comercial: 0.8,
    default: 1.0,
  }
  const threshold = thresholds[typology] ?? thresholds.default
  const excess_pct = ((intensity / threshold) - 1) * 100

  // Severidad y probabilidad según nivel oficial de sequía
  let sev, lik, horizon
  if (level === 'emergencia')          { sev = 4; lik = 5; horizon = 'short'  }
  else if (level === 'excepcionalidad') { sev = 3; lik = 4; horizon = 'short'  }
  else if (level === 'alerta')          { sev = 2; lik = 4; horizon = 'medium' }
  else return null

  // Ajuste si el edificio supera claramente el umbral GRESB
  if (intensity > threshold * 1.5) sev = Math.min(sev + 1, 5)

  const now = new Date()
  const region = waterStressInfo.region || 'demarcación hidrográfica del activo'

  return {
    detector_id: 'D3.estres_hidrico',
    type: 'risk',
    esrs_datapoint: 'E3-4',
    category: 'water-stress',
    period_year: now.getFullYear(),
    period_month: now.getMonth() + 1,
    severity: sev,
    likelihood: lik,
    time_horizon: horizon,
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: [],
    literal_quote: `Región hídrica: ${region}. Nivel de estrés declarado: ${level}. Fuente: ${waterStressInfo.source || 'MITECO · Boletín Hidrológico'}${waterStressInfo.source_date ? ' (' + waterStressInfo.source_date + ')' : ''}. Intensidad hídrica del activo: ${intensity.toFixed(2)} m³/m²·año sobre umbral GRESB tipología ${typology}: ${threshold} m³/m²·año.`,
    detector_output: {
      criterion: 'Reglamento (UE) 2020/852, Art. 12 DNSH «Uso sostenible y protección de los recursos hídricos y marinos» + ESRS E3-4 «Consumo de agua en áreas con estrés hídrico» + Real Decreto 907/2007 Plan Hidrológico Nacional. Activo situado en zona con nivel de estrés hídrico ≥ Alerta según Plan Especial de Sequías vigente.',
      stress_level: level,
      region,
      typology,
      water_m3_annual: water_m3,
      area_m2: m2,
      intensity_m3_m2_year: Math.round(intensity * 100) / 100,
      threshold_m3_m2_year: threshold,
      excess_pct: Math.round(excess_pct * 10) / 10,
      months_covered: period.period.months,
      source: waterStressInfo.source || null,
      source_date: waterStressInfo.source_date || null,
      note: `Consumo hídrico ${intensity > threshold ? 'por encima' : 'dentro'} del umbral sectorial GRESB Water Indicator. Zona con estrés hídrico declarado oficialmente — recomendar plan de reducción: reutilización de pluviales (RD 1620/2007), monitorización continua, reducción de caudales sanitarios en cocinas y aseos, y reporte anual del datapoint ESRS E3-4 al inversor.`,
    },
  }
}

// ── D1 · Intensidad CO₂e creciente ───────────────────────────────────────────
// Impact · ESRS E1-6
export function detectIntensidadCO2(rows, settings) {
  const m2 = Number(settings?.gross_area_m2) || 0
  const baseline = Number(settings?.baseline_co2_kgm2_year) || 0
  if (m2 <= 0 || baseline <= 0) return null

  const win = rollingWindow(rows, 12)
  if (!win || win.monthsCovered < 6) return null

  const scope1 = sumField(win.rows, 'scope1_kgco2e')
  const scope2 = sumField(win.rows, 'scope2_kgco2e')
  const totalKg = scope1 + scope2
  if (totalKg <= 0) return null

  // Annualize if we have < 12 months
  const annualized = totalKg * (12 / win.monthsCovered)
  const intensity = annualized / m2
  const excessPct = (intensity - baseline) / baseline
  const sev = severityFromExcessPct(excessPct)
  if (sev === 0) return null

  const summary = buildPeriodSummary(win.rows)

  return {
    detector_id: 'D1.intensidad_co2',
    type: 'impact',
    esrs_datapoint: 'E1-6',
    category: 'climate',
    period_year: win.endYear,
    period_month: win.endMonth,
    severity: sev,
    likelihood: null,
    time_horizon: 'short',
    materiality: 'impact',
    financial_estimate_eur: null,
    evidence_rows: evidenceIds(win.rows),
    literal_quote: summary?.text || null,
    detector_output: {
      months_covered: win.monthsCovered,
      annual_co2_kg: Math.round(annualized),
      intensity_kg_m2: +intensity.toFixed(2),
      baseline_kg_m2: baseline,
      excess_pct: +(excessPct * 100).toFixed(1),
      period_summary: summary ? { period: summary.period, totals: summary.totals, invoices: summary.invoices } : null,
    },
  }
}

// ── D7 · Exposición EU ETS / precio carbono ──────────────────────────────────
// Risk (transition) · ESRS E1-9
export function detectEUETS(rows, settings) {
  const factor = Number(settings?.gas_emission_factor) || 0.2016
  const eua = Number(settings?.eua_price_eur_per_t) || 85
  if (eua <= 0 || factor <= 0) return null

  const win = rollingWindow(rows, 12)
  if (!win || win.monthsCovered < 3) return null

  const gasKwh = sumField(win.rows, 'gas_kwh')
  if (gasKwh <= 0) return null  // no gas → no ETS exposure

  const annualGas = gasKwh * (12 / win.monthsCovered)
  const tCO2 = (annualGas * factor) / 1000
  const exposureEur = tCO2 * eua
  if (exposureEur < 100) return null  // immaterial

  const gasRows = win.rows.filter(r => Number(r.gas_kwh) > 0)
  const summary = buildPeriodSummary(gasRows)

  return {
    detector_id: 'D7.eu_ets',
    type: 'risk',
    esrs_datapoint: 'E1-9',
    category: 'climate-transition',
    period_year: win.endYear,
    period_month: win.endMonth,
    severity: severityFromEur(exposureEur),
    likelihood: 4,           // EU ETS scope extension to buildings is highly likely
    time_horizon: 'medium',
    materiality: 'financial',
    financial_estimate_eur: Math.round(exposureEur),
    evidence_rows: evidenceIds(gasRows),
    literal_quote: summary?.text || null,
    detector_output: {
      months_covered: win.monthsCovered,
      annual_gas_kwh: Math.round(annualGas),
      emission_factor: factor,
      tCO2_year: +tCO2.toFixed(2),
      eua_price: eua,
      period_summary: summary ? { period: summary.period, totals: summary.totals, invoices: summary.invoices } : null,
    },
  }
}

// ── D12 · Excedente FV monetizable ───────────────────────────────────────────
// Opportunity · ESRS E1-3
export function detectExcedenteFV(rows, settings) {
  if (!settings?.has_pv) return null
  const tariff = Number(settings?.fv_export_tariff_eur_kwh) || 0.06

  const win = rollingWindow(rows, 12)
  if (!win || win.monthsCovered < 3) return null

  // Heuristic: months where FV generation > electricity grid consumption
  // imply potential excess (full hourly data would be more accurate; this is MVP).
  let excessKwh = 0
  const evidence = []
  for (const r of win.rows) {
    const fv = Number(r.energy_kwh_renewable) || 0
    const grid = Number(r.electricity_kwh) || 0
    if (fv > grid && fv > 0) {
      excessKwh += (fv - grid)
      evidence.push(r)
    }
  }
  if (excessKwh < 100) return null  // immaterial

  const annualExcess = excessKwh * (12 / win.monthsCovered)
  const monetization = annualExcess * tariff

  const summary = buildPeriodSummary(evidence)

  return {
    detector_id: 'D12.excedente_fv',
    type: 'opportunity',
    esrs_datapoint: 'E1-3',
    category: 'renewables',
    period_year: win.endYear,
    period_month: win.endMonth,
    severity: severityFromEur(monetization),
    likelihood: 5,
    time_horizon: 'short',
    materiality: 'financial',
    financial_estimate_eur: Math.round(monetization),
    evidence_rows: evidenceIds(evidence),
    literal_quote: summary?.text || null,
    detector_output: {
      months_covered: win.monthsCovered,
      annual_excess_kwh: Math.round(annualExcess),
      tariff_eur_kwh: tariff,
      period_summary: summary ? { period: summary.period, totals: summary.totals, invoices: summary.invoices } : null,
    },
  }
}

// ── D13 · Alineación Taxonomía UE 7.7 (Acquisition & ownership of buildings) ─
// Opportunity · ESRS E1-3 + EU Taxonomy
//
// Source: Reglamento Delegado (UE) 2021/2139, Anexo I, sección 7.7
//   Criterio (a) — Edificios construidos antes del 31/12/2020:
//     "el edificio dispone, como mínimo, de un Certificado de Eficiencia
//      Energética (CEE) de clase A".
//   Criterio (b) — Edificios construidos a partir del 01/01/2021:
//     debe cumplir el criterio de la actividad 7.1:
//     "Primary Energy Demand ≤ NZEB national threshold × 0,9".
//     En España, el NZEB nacional para Otros usos se materializa en CTE
//     DB-HE como el valor Cep,B-C (Verificación CTE-HE0 generada por HULC) =
//     "Valor máximo de consumo de energía primaria no renovable para la
//     clase B" para ese edificio (per-edificio, calculado según su geometría
//     y zona climática).
//
// Este detector NUNCA usa umbrales proxy. Todos los valores se introducen
// desde documentos oficiales del proyecto con su cita literal.
export function detectTaxonomia77(rows, settings) {
  const year = Number(settings?.construction_year)
  if (!year) return null

  // Datos operativos comunes a ambos casos (sólo contexto auditable)
  const m2 = Number(settings?.gross_area_m2) || 0
  const win = rollingWindow(rows, 12)
  let annualKwh = null, eui = null
  if (win && win.monthsCovered >= 6 && m2 > 0) {
    const elec = sumField(win.rows, 'electricity_kwh')
    const gas  = sumField(win.rows, 'gas_kwh')
    const dh   = sumField(win.rows, 'district_heat_kwh')
    const total = elec + gas + dh
    if (total > 0) {
      annualKwh = Math.round(total * (12 / win.monthsCovered))
      eui = +(annualKwh / m2).toFixed(1)
    }
  }
  const endYear  = win?.endYear  || new Date().getFullYear()
  const endMonth = win?.endMonth || new Date().getMonth() + 1
  const evidenceRowsList = win ? evidenceIds(win.rows) : []
  const summary = win ? buildPeriodSummary(win.rows) : null
  const literalQuote = summary?.text || null
  const periodSummaryShared = summary ? { period: summary.period, totals: summary.totals, invoices: summary.invoices } : null

  // ─── Caso (a) — edificios pre-2020 ─────────────────────────────────────────
  if (year <= 2020) {
    const cls = String(settings?.cee_class || '').toUpperCase()
    if (cls !== 'A') return null  // sólo clase A dispara

    return {
      detector_id: 'D13.taxonomia_7_7',
      type: 'opportunity',
      esrs_datapoint: 'E1-3',
      category: 'eu-taxonomy',
      period_year: endYear,
      period_month: endMonth,
      severity: 4,
      likelihood: 5,
      time_horizon: 'short',
      materiality: 'financial',
      financial_estimate_eur: null,
      evidence_rows: evidenceRowsList,
      literal_quote: literalQuote,
      detector_output: {
        sub_criterion: '7.7(a)',
        criterion: 'Reglamento Delegado (UE) 2021/2139, Anexo I, sección 7.7, criterio (a): EPC class ≥ A para edificios construidos antes del 31/12/2020',
        construction_year: year,
        cee_class: cls,
        cee_class_source: settings.cee_class_source || null,
        operational_annual_kwh: annualKwh,
        operational_eui_kwh_m2: eui,
        months_covered: win?.monthsCovered || 0,
        note: 'Alineación verificada por criterio normativo (clase CEE). Los datos operacionales se muestran para contexto auditable pero no son condición del disparo.',
        period_summary: periodSummaryShared,
      },
    }
  }

  // ─── Caso (b) — edificios post-2020 ────────────────────────────────────────
  // Necesita: Cep,nren diseñado + NZEB threshold (ambos con su cita).
  const cep  = Number(settings?.cep_nren_designed_kwh_m2)
  const nzeb = Number(settings?.nzeb_threshold_kwh_m2)
  if (!cep || cep <= 0 || !nzeb || nzeb <= 0) return null

  const threshold71 = nzeb * 0.9   // Taxonomía 7.1: PED ≤ NZEB × 0,9
  if (cep > threshold71) return null  // no cumple → no es oportunidad

  const headroomPct = (threshold71 - cep) / threshold71
  let sev = 1
  if (headroomPct >= 0.10) sev = 2
  if (headroomPct >= 0.20) sev = 3
  if (headroomPct >= 0.30) sev = 4
  if (headroomPct >= 0.50) sev = 5

  return {
    detector_id: 'D13.taxonomia_7_7',
    type: 'opportunity',
    esrs_datapoint: 'E1-3',
    category: 'eu-taxonomy',
    period_year: endYear,
    period_month: endMonth,
    severity: sev,
    likelihood: 5,
    time_horizon: 'short',
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: evidenceRowsList,
    literal_quote: literalQuote,
    detector_output: {
      sub_criterion: '7.7(b)',
      criterion: 'Reglamento Delegado (UE) 2021/2139, Anexo I, sección 7.7, criterio (b): cumple criterio de actividad 7.1 → PED ≤ NZEB × 0,9 para edificios construidos a partir del 01/01/2021',
      construction_year: year,
      cep_nren_designed_kwh_m2: cep,
      cep_nren_designed_source: settings.cep_nren_designed_source || null,
      nzeb_threshold_kwh_m2: nzeb,
      nzeb_threshold_source: settings.nzeb_threshold_source || null,
      taxonomy_71_threshold_kwh_m2: +threshold71.toFixed(2),
      compliance_margin_pct: +(headroomPct * 100).toFixed(1),
      operational_annual_kwh: annualKwh,
      operational_eui_kwh_m2: eui,
      months_covered: win?.monthsCovered || 0,
      note: 'Alineación verificada por cálculo normativo (Cep ≤ NZEB × 0,9). Cep y NZEB extraídos literalmente de documentos oficiales del proyecto. Los datos operacionales se muestran para contexto auditable pero no son condición del disparo.',
      period_summary: periodSummaryShared,
    },
  }
}

// ── D14 · Performance Gap operacional (Cep,nren real vs CEE) ─────────────────
// Impact · ESRS E1-6
//
// Compara el consumo real anualizado, convertido a energía primaria no
// renovable usando los factores de paso oficiales del IDAE, contra el
// Cep,nren del CEE del edificio. Un desvío ≥10% dispara Impacto.
//
// Factores de paso (Documento oficial IDAE, 20 junio 2016):
// "Factores de emisión de CO2 y coeficientes de paso a energía primaria de
// diferentes fuentes de energía final consumidas en el sector edificios en
// España". Valores utilizados aquí:
//   · Electricidad convencional peninsular: 1,954 kWh_ep_nren/kWh_final
//   · Gas natural: 1,190 kWh_ep_nren/kWh_final
//
// La FV autoconsumida NO cuenta como energía primaria no renovable (es
// renovable en origen). El consumo de red ya reflejaba el neto tras el
// autoconsumo, así que no se resta nada adicional.
export function detectPerformanceGap(rows, settings) {
  const m2 = Number(settings?.gross_area_m2) || 0
  const baselineCep = Number(settings?.baseline_eui_kwh_m2_year) || 0
  if (m2 <= 0 || baselineCep <= 0) return null

  const win = rollingWindow(rows, 12)
  if (!win || win.monthsCovered < 6) return null

  const F_ELEC = 1.954
  const F_GAS  = 1.190

  const elec = sumField(win.rows, 'electricity_kwh')
  const gas  = sumField(win.rows, 'gas_kwh')
  const totalFinal = elec + gas
  if (totalFinal <= 0) return null

  const annualFinal    = totalFinal * (12 / win.monthsCovered)
  const annualCepNren  = (elec * F_ELEC + gas * F_GAS) * (12 / win.monthsCovered)
  const realCep        = annualCepNren / m2
  const ratio          = realCep / baselineCep

  if (ratio < 1.10) return null  // dentro de tolerancia — no dispara

  let sev = 1
  if (ratio >= 1.30) sev = 2
  if (ratio >= 1.50) sev = 3
  if (ratio >= 2.00) sev = 4
  if (ratio >= 3.00) sev = 5

  const excessPct = +((ratio - 1) * 100).toFixed(1)
  const summary = buildPeriodSummary(win.rows)

  return {
    detector_id: 'D14.performance_gap',
    type: 'impact',
    esrs_datapoint: 'E1-6',
    category: 'climate-performance',
    period_year: win.endYear,
    period_month: win.endMonth,
    severity: sev,
    likelihood: null,
    time_horizon: 'short',
    materiality: 'impact',
    financial_estimate_eur: null,
    evidence_rows: evidenceIds(win.rows),
    literal_quote: summary?.text || null,
    detector_output: {
      criterion: 'Performance gap operacional: Cep,nren real vs Cep,nren del CEE (edificio evaluado). El CEE se calcula con condiciones normativas de uso; un desvío operacional documenta el gap para reporting CSRD/GRESB pero NO invalida la clasificación CEE del activo.',
      factor_source: 'IDAE, 20 junio 2016. "Factores de emisión de CO2 y coeficientes de paso a energía primaria de diferentes fuentes de energía final consumidas en el sector edificios en España". Electricidad convencional peninsular = 1,954 · Gas natural = 1,190.',
      annual_final_kwh: Math.round(annualFinal),
      annual_cep_nren_kwh: Math.round(annualCepNren),
      real_cep_nren_kwh_m2: +realCep.toFixed(1),
      baseline_cep_nren_kwh_m2: baselineCep,
      excess_pct: excessPct,
      months_covered: win.monthsCovered,
      note: 'La FV autoconsumida no cuenta en Cep,nren (es renovable). El consumo de red ya es neto de autoconsumo FV. Un desvío 2-4× entre CEE y realidad es habitual en hoteles operativos (perfil normativo ≠ uso real intensivo con ocupación + piscina + restaurante).',
      period_summary: summary ? { period: summary.period, totals: summary.totals, invoices: summary.invoices } : null,
    },
  }
}

// ── D8 · Riesgo ZBE / regulatorio de transición climática ────────────────────
// Risk (transition) · ESRS E1-9
//
// Marco: Ley 7/2021 art. 14.3 obliga a los municipios ≥ 50.000 habitantes a
// implantar ZBE (Zonas de Bajas Emisiones). Real Decreto 1052/2022 regula
// delimitación y señalización. Un activo situado en un municipio con ZBE
// enfrenta riesgo de transición: restricciones a vehículos de la flota,
// combustión residual (calderas gas), reformas obligatorias de urbanismo.
//
// El detector NO consulta APIs en tiempo real. Compara settings.municipality
// contra la tabla lookup public.zbe_municipios. La comprobación real (lookup
// en BD) se hace desde el componente que inyecta zbeInfo como argumento.
//
// zbeInfo: { status, zbe_name, zbe_start_date, source } o null.
export function detectRiesgoZBE(rows, settings, zbeInfo) {
  if (!zbeInfo || !zbeInfo.status || zbeInfo.status === 'none') return null
  const status = zbeInfo.status
  const municipality = settings?.municipality || zbeInfo.name || 'municipio del activo'

  // Riesgo adicional si el edificio tiene combustión de gas natural
  const hasGas = (rows || []).some(r => Number(r.gas_kwh) > 0)

  // Severidad base por estado de la ZBE
  let sev
  if (status === 'active')      sev = 3   // ZBE en vigor
  else if (status === 'pilot')  sev = 2   // proyecto piloto activo
  else if (status === 'announced') sev = 2 // anunciada pero no vigente
  else return null

  // Ajuste si hay combustión (más expuesto al endurecimiento normativo)
  if (hasGas && sev < 5) sev += 1

  const now = new Date()
  const noteBits = []
  if (status === 'active')      noteBits.push('ZBE activa en el municipio del activo')
  if (status === 'announced')   noteBits.push('ZBE anunciada — fecha estimada de entrada en vigor: ' + (zbeInfo.zbe_start_date || 'por determinar'))
  if (hasGas)                    noteBits.push('El activo consume gas natural — mayor exposición a reformas de descarbonización de la envolvente')
  if (!hasGas)                   noteBits.push('El activo NO consume combustibles fósiles in situ — reduce parcialmente el riesgo, pero persiste el impacto sobre movilidad y suministros logísticos')

  return {
    detector_id: 'D8.riesgo_zbe',
    type: 'risk',
    esrs_datapoint: 'E1-9',
    category: 'climate-transition',
    period_year: now.getFullYear(),
    period_month: now.getMonth() + 1,
    severity: sev,
    likelihood: status === 'active' ? 5 : 4,
    time_horizon: status === 'active' ? 'short' : 'medium',
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: [],
    literal_quote: `Municipio del activo: ${municipality}. Estado ZBE: ${status}. ZBE: ${zbeInfo.zbe_name || 'sin nombre local'}${zbeInfo.zbe_start_date ? ' (vigor desde ' + zbeInfo.zbe_start_date + ')' : ''}. Fuente: ${zbeInfo.source || 'MITECO/Ley 7/2021'}.`,
    detector_output: {
      criterion: 'Ley 7/2021, de 20 de mayo, de cambio climático y transición energética, art. 14.3 + Real Decreto 1052/2022 regulador de las ZBE. Activo situado en municipio con obligación de implantar Zona de Bajas Emisiones — riesgo regulatorio de transición climática.',
      municipality,
      zbe_status: status,
      zbe_name: zbeInfo.zbe_name || null,
      zbe_start_date: zbeInfo.zbe_start_date || null,
      has_gas_combustion: hasGas,
      source: zbeInfo.source || null,
      note: noteBits.join(' · '),
    },
  }
}

// ── D9 · Obsolescencia HVAC (eficiencia año/año normalizada por grados-día) ──
// Risk · ESRS E1-9
//
// Compara la eficiencia HVAC del último año completo contra el año anterior,
// normalizando el consumo por grados-día (HDD+CDD) para descontar el efecto
// climático. Un empeoramiento sostenido de kWh/GD indica que el sistema
// (bomba de calor, split, chiller...) está perdiendo rendimiento por edad,
// suciedad, refrigerante bajo o dimensionado inadecuado.
//
// Requiere:
//   · settings.gross_area_m2
//   · ≥12 meses de building_sensors del año actual Y otros 12 del año anterior
//   · weather_hdd_cdd cargados (D10 se cargan juntos)
//
// El detector recibe hddCddMap: Map<'YYYY-MM', {hdd, cdd}> como argumento
// adicional inyectado por runAllDetectors si está disponible.
export function detectObsolescenciaHVAC(rows, settings, weatherMap) {
  if (!weatherMap || weatherMap.size === 0) return null
  const m2 = Number(settings?.gross_area_m2) || 0
  if (m2 <= 0) return null

  // Agrupar consumo por año-mes (electricidad + gas + calefacción urbana)
  const byMonth = new Map()
  for (const r of rows || []) {
    const key = `${r.reading_year}-${String(r.reading_month).padStart(2,'0')}`
    const kwh = (Number(r.electricity_kwh) || 0) + (Number(r.gas_kwh) || 0) + (Number(r.district_heat_kwh) || 0)
    byMonth.set(key, kwh)
  }

  // Necesitamos dos años completos (12 meses cada uno) de consumo Y meteo
  const yearBuckets = new Map()
  for (const [key, kwh] of byMonth) {
    const w = weatherMap.get(key)
    if (!w || (w.hdd + w.cdd) <= 0) continue
    const y = parseInt(key.slice(0, 4))
    if (!yearBuckets.has(y)) yearBuckets.set(y, { kwh: 0, gd: 0, months: 0 })
    const b = yearBuckets.get(y)
    b.kwh += kwh
    b.gd += (w.hdd + w.cdd)
    b.months += 1
  }
  const complete = [...yearBuckets.entries()].filter(([, b]) => b.months === 12).sort((a, b) => b[0] - a[0])
  if (complete.length < 2) return null

  const [yr_actual, actualB] = complete[0]
  const [yr_prev, prevB] = complete[1]
  const eff_actual = actualB.kwh / actualB.gd  // kWh por grado-día
  const eff_prev   = prevB.kwh / prevB.gd
  const change_pct = (eff_actual - eff_prev) / eff_prev

  if (change_pct < 0.05) return null  // mejora o estable — no dispara

  let sev = 1
  if (change_pct >= 0.10) sev = 2
  if (change_pct >= 0.15) sev = 3
  if (change_pct >= 0.25) sev = 4
  if (change_pct >= 0.40) sev = 5

  return {
    detector_id: 'D9.obsolescencia_hvac',
    type: 'risk',
    esrs_datapoint: 'E1-9',
    category: 'climate-transition',
    period_year: yr_actual,
    period_month: 12,
    severity: sev,
    likelihood: 4,
    time_horizon: 'medium',
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: [],
    literal_quote: `Consumo HVAC ${yr_actual}: ${Math.round(actualB.kwh).toLocaleString('es-ES')} kWh en ${Math.round(actualB.gd)} grados-día → ${eff_actual.toFixed(2)} kWh/GD. Consumo HVAC ${yr_prev}: ${Math.round(prevB.kwh).toLocaleString('es-ES')} kWh en ${Math.round(prevB.gd)} grados-día → ${eff_prev.toFixed(2)} kWh/GD. Degradación interanual: ${(change_pct * 100).toFixed(1)}%.`,
    detector_output: {
      criterion: 'Comparación de eficiencia HVAC año/año normalizada por grados-día (HDD base 15,5°C + CDD base 22°C). Un aumento sostenido del ratio kWh/GD indica pérdida de eficiencia del sistema climático.',
      year_actual: yr_actual,
      year_previous: yr_prev,
      efficiency_actual_kwh_per_gd: +eff_actual.toFixed(2),
      efficiency_previous_kwh_per_gd: +eff_prev.toFixed(2),
      change_pct: +(change_pct * 100).toFixed(1),
      hdd_actual: Math.round(actualB.gd),
      hdd_previous: Math.round(prevB.gd),
      station_source: 'AEMET OpenData (estación configurada por proyecto)',
      note: 'La degradación puede deberse a envejecimiento del sistema, mantenimiento insuficiente, cambios de uso o refrigerante bajo. Recomendable auditoría técnica de las unidades climáticas.',
    },
  }
}

// ── D10 · Calor crónico — tendencia CDD histórica ────────────────────────────
// Risk · ESRS E1-9
//
// Detecta si el activo está expuesto a una tendencia creciente de demanda
// de refrigeración (más CDD año tras año) que anticipa mayores costes
// operativos y de renovación de equipos. Compara CDD del último año completo
// contra la media de los 5 años anteriores.
//
// Requiere ≥6 años de datos en weather_hdd_cdd.
export function detectCalorCronico(rows, settings, weatherMap) {
  if (!weatherMap || weatherMap.size === 0) return null

  const yearCdd = new Map()
  for (const [key, w] of weatherMap) {
    const y = parseInt(key.slice(0, 4))
    const cdd = Number(w.cdd) || 0
    if (!yearCdd.has(y)) yearCdd.set(y, { cdd: 0, months: 0 })
    yearCdd.get(y).cdd += cdd
    yearCdd.get(y).months += 1
  }
  const completeYears = [...yearCdd.entries()].filter(([, v]) => v.months === 12).sort((a, b) => b[0] - a[0])
  if (completeYears.length < 6) return null

  const [yr_actual, actual] = completeYears[0]
  const baseline = completeYears.slice(1, 6)  // 5 años previos
  const avg_baseline = baseline.reduce((a, [, v]) => a + v.cdd, 0) / baseline.length

  const ratio = actual.cdd / avg_baseline
  if (ratio < 1.10) return null  // sin exceso significativo

  let sev = 1
  if (ratio >= 1.15) sev = 2
  if (ratio >= 1.25) sev = 3
  if (ratio >= 1.40) sev = 4
  if (ratio >= 1.60) sev = 5

  return {
    detector_id: 'D10.calor_cronico',
    type: 'risk',
    esrs_datapoint: 'E1-9',
    category: 'climate-physical-chronic',
    period_year: yr_actual,
    period_month: 12,
    severity: sev,
    likelihood: 4,
    time_horizon: 'medium',
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: [],
    literal_quote: `CDD ${yr_actual} = ${actual.cdd.toFixed(0)} grados-día (base 22°C). Media ${baseline.length} años anteriores (${baseline[baseline.length-1][0]}-${baseline[0][0]}) = ${avg_baseline.toFixed(0)} GD. Incremento: ${((ratio - 1) * 100).toFixed(1)}%.`,
    detector_output: {
      criterion: 'Tendencia CDD (Cooling Degree Days base 22°C) del año actual contra la media móvil de 5 años previos. Alineado con TCFD/ESRS E1-9 para riesgos físicos crónicos climáticos.',
      year_actual: yr_actual,
      cdd_actual: Math.round(actual.cdd),
      baseline_years: baseline.map(([y]) => y),
      cdd_avg_baseline: Math.round(avg_baseline),
      ratio: +ratio.toFixed(2),
      excess_pct: +((ratio - 1) * 100).toFixed(1),
      station_source: 'AEMET OpenData (estación configurada por proyecto)',
      note: 'Un CDD creciente sostenidamente implica más horas de operación de refrigeración, mayor consumo eléctrico en verano, y aceleración del envejecimiento de los equipos. Considerar en el plan de renovación HVAC medidas pasivas (aislamiento, sombreamiento) + activas (mayor rendimiento estacional).',
    },
  }
}

// ── D11 · Riesgo físico agudo — Inundación ARPSI ─────────────────────────────
// Risk (physical acute) · ESRS E1-9
//
// Consulta al SNCZI de MITECO (Sistema Nacional de Cartografía de Zonas
// Inundables, Directiva 2007/60/CE). El detector no llama al WMS en vivo
// (el WMS tiene CORS restrictivo desde navegador) — lee el resultado ya
// verificado y guardado en operational_iro_settings.arpsi_status.
//
// La comprobación se hace vía script/edge function en el servidor (una vez
// por proyecto, se re-verifica cada 6 años porque los ARPSI se revisan
// en ciclos EPRI).
export function detectRiesgoInundacion(rows, settings) {
  const status = settings?.arpsi_status
  if (!status || status === 'not_checked' || status === 'not_at_risk') return null

  // Severidad por periodo de retorno (T10 = probabilidad alta, T500 = baja)
  let sev, likelihood, retornoLabel
  if (status === 'at_risk_T10')       { sev = 5; likelihood = 5; retornoLabel = '10 años (alta probabilidad)' }
  else if (status === 'at_risk_T100') { sev = 4; likelihood = 4; retornoLabel = '100 años (probabilidad media)' }
  else if (status === 'at_risk_T500') { sev = 3; likelihood = 3; retornoLabel = '500 años (baja probabilidad)' }
  else return null

  const now = new Date()

  return {
    detector_id: 'D11.riesgo_inundacion',
    type: 'risk',
    esrs_datapoint: 'E1-9',
    category: 'climate-physical-acute',
    period_year: now.getFullYear(),
    period_month: now.getMonth() + 1,
    severity: sev,
    likelihood,
    time_horizon: 'medium',
    materiality: 'financial',
    financial_estimate_eur: null,
    evidence_rows: [],
    literal_quote: settings.arpsi_source || null,
    detector_output: {
      criterion: 'Directiva 2007/60/CE (Directiva de Inundaciones) + Real Decreto 903/2010, transpuesta en España como el Sistema Nacional de Cartografía de Zonas Inundables (SNCZI) de MITECO. Un activo clasificado dentro de una ARPSI (Área con Riesgo Potencial Significativo de Inundación) presenta riesgo físico agudo reportable a ESRS E1-9.',
      source_wms: 'MITECO SNCZI (https://servicios.idee.es/wms-inspire/riesgos-naturales/inundaciones) — capas NZ.Flood.FluvialT10 / T100 / T500 y NZ.Flood.MarinaT100 / T500.',
      arpsi_status: status,
      periodo_retorno: retornoLabel,
      check_date: settings.arpsi_check_date || null,
      arpsi_source_cita: settings.arpsi_source || null,
      note: 'Los ARPSI se revisan en ciclos EPRI de 6 años. La comprobación debe repetirse tras cada actualización oficial de MITECO. En caso de disparo, considerar en el plan de mitigación medidas físicas (protecciones perimetrales, elevación equipos críticos) y contratación de seguro específico.',
    },
  }
}

// ── Public API ───────────────────────────────────────────────────────────────
export const PHASE_1_DETECTORS = [
  { id: 'D1.intensidad_co2',    label: 'Intensidad CO₂e ↑',           fn: detectIntensidadCO2 },
  { id: 'D3.estres_hidrico',    label: 'Estrés hídrico regional',      fn: detectEstresHidrico,   needsWaterStress: true },
  { id: 'D7.eu_ets',            label: 'Exposición EU ETS',            fn: detectEUETS },
  { id: 'D8.riesgo_zbe',        label: 'Riesgo ZBE / normativo',       fn: detectRiesgoZBE, needsZbe: true },
  { id: 'D9.obsolescencia_hvac', label: 'Obsolescencia HVAC año/año',  fn: detectObsolescenciaHVAC, needsWeather: true },
  { id: 'D10.calor_cronico',    label: 'Calor crónico (tendencia CDD)', fn: detectCalorCronico,     needsWeather: true },
  { id: 'D11.riesgo_inundacion', label: 'Riesgo inundación (ARPSI)',   fn: detectRiesgoInundacion },
  { id: 'D12.excedente_fv',     label: 'Excedente FV monetizable',     fn: detectExcedenteFV },
  { id: 'D13.taxonomia_7_7',    label: 'Alineación Taxonomía 7.7',     fn: detectTaxonomia77 },
  { id: 'D14.performance_gap',  label: 'Performance Gap operacional',  fn: detectPerformanceGap },
]

// weatherMap: Map<'YYYY-MM', { hdd, cdd, avg_temp_c }> — opcional; usan D9/D10.
// zbeInfo: { status, zbe_name, zbe_start_date, source, name } — opcional; usa D8.
// waterStressInfo: { level, region, source, source_date } — opcional; usa D3.
export function runAllDetectors(rows, settings, weatherMap, zbeInfo, waterStressInfo) {
  const out = []
  for (const d of PHASE_1_DETECTORS) {
    try {
      let iro
      if (d.needsWeather)          iro = d.fn(rows, settings, weatherMap)
      else if (d.needsZbe)         iro = d.fn(rows, settings, zbeInfo)
      else if (d.needsWaterStress) iro = d.fn(rows, settings, waterStressInfo)
      else                          iro = d.fn(rows, settings)
      if (iro) out.push(iro)
    } catch (err) {
      console.warn(`[iroDetectors] ${d.id} failed:`, err?.message || err)
    }
  }
  return out
}
